/* Panels from the Deadlock menu, rebuilt in HTML for the homepage.

   The feature cards used to be an icon, a headline and a decorative pill that
   said "Aimbot" -- nothing a visitor could not have guessed. These show the
   actual controls, with the labels, defaults and sub-headings copied from the
   captures in public/menu (menu-01: Aim > Aimbot), and the colours sampled
   from them: page #0b0e16, panel #1b2230, toggle #4d8eb1 / #b8d6de, slider
   #5293b6 on #242e3a, dropdown #222b3a.

   `live` flips once the card scrolls into view: toggles switch on and sliders
   glide to their value, so the panel reads as something being set up rather
   than a picture of one. Reduced motion gets the end state with no travel. */

const C = {
  panel: "#1b2230",
  head: "#1f2835",
  border: "#2a3442",
  text: "#d4dbe3",
  sub: "#6f7b8b",
  track: "#242e3a",
  fill: "#5293b6",
  thumb: "#bccbd1",
  toggleOn: "#4d8eb1",
  knobOn: "#b8d6de",
  toggleOff: "#2f3947",
  knobOff: "#7d8896",
  select: "#222b3a",
};

export type Row =
  | { kind: "toggle"; label: string; on: boolean }
  | { kind: "slider"; label: string; value: number; max: number; display: string }
  | { kind: "select"; label: string; value: string };

const Toggle = ({ on }: { on: boolean }) => (
  <span
    className="relative inline-block h-[18px] w-[34px] shrink-0 rounded-full transition-colors duration-500 motion-reduce:transition-none"
    style={{ background: on ? C.toggleOn : C.toggleOff }}
  >
    <span
      className="absolute top-[3px] h-3 w-3 rounded-full transition-all duration-500 motion-reduce:transition-none"
      style={{ left: on ? 19 : 3, background: on ? C.knobOn : C.knobOff }}
    />
  </span>
);

const Slider = ({ pct, display }: { pct: number; display: string }) => (
  <span className="flex w-[58%] shrink-0 items-center gap-2">
    <span className="w-9 shrink-0 text-right tabular-nums" style={{ color: C.sub }}>
      {display}
    </span>
    <span className="relative h-[3px] flex-1 rounded-full" style={{ background: C.track }}>
      <span
        className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700 ease-out motion-reduce:transition-none"
        style={{ width: `${pct}%`, background: C.fill }}
      />
      <span
        className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full transition-[left] duration-700 ease-out motion-reduce:transition-none"
        style={{ left: `${pct}%`, background: C.thumb }}
      />
    </span>
  </span>
);

const Select = ({ value }: { value: string }) => (
  <span
    className="inline-flex min-w-[5.5rem] items-center justify-between gap-2 rounded-md border px-2.5 py-1"
    style={{ background: C.select, borderColor: C.border, color: C.text }}
  >
    {value}
    <svg className="h-3 w-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 9l6 6 6-6" />
    </svg>
  </span>
);

export const MenuPanel = ({
  title,
  sub,
  rows,
  live,
}: {
  title: string;
  sub: string;
  rows: Row[];
  /** Once true, the controls move to their set values. */
  live: boolean;
}) => (
  <div
    /* Decorative: the card's heading and copy carry the meaning. */
    aria-hidden
    className="w-full overflow-hidden rounded-xl border text-[0.72rem] shadow-xl shadow-black/40"
    style={{ background: C.panel, borderColor: C.border, color: C.text }}
  >
    <div className="flex items-center gap-2 border-b px-3.5 py-2" style={{ background: C.head, borderColor: C.border }}>
      <span className="text-[0.62rem] font-bold uppercase tracking-[0.14em]">{title}</span>
      <span className="text-[0.62rem]" style={{ color: C.sub }}>
        {sub}
      </span>
    </div>
    <div className="space-y-2.5 px-3.5 py-3">
      {rows.map((row) => (
        <div key={row.label} className="flex min-h-[20px] items-center justify-between gap-3">
          <span className="truncate">{row.label}</span>
          {row.kind === "toggle" && <Toggle on={live && row.on} />}
          {row.kind === "slider" && (
            <Slider pct={live ? (row.value / row.max) * 100 : 0} display={live ? row.display : "0"} />
          )}
          {row.kind === "select" && <Select value={row.value} />}
        </div>
      ))}
    </div>
  </div>
);
